import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ChangePasswordForm } from './ChangePasswordForm';
import { useUpdateAuthenticatedUser } from '../../hooks/api/use-users.hook';

export const ChangePassword = () => {
  const navigate = useNavigate();

  const [updateAuthenticatedUser, { error, loading }] = useUpdateAuthenticatedUser();

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<ChangePasswordForm>({ resolver: classValidatorResolver(ChangePasswordForm) });

  const onSubmit: SubmitHandler<ChangePasswordForm> = async ({ currentPassword, newPassword, confirmPassword }) => {
    if (newPassword !== confirmPassword) {
      setError('confirmPassword', { message: 'Password does not match' });
      return;
    }

    await updateAuthenticatedUser({ currentPassword, newPassword });
  };

  const renderContent = () => {
    if (isSubmitSuccessful) {
      return <Alert severity="success">Password changed successfully!</Alert>;
    }

    return (
      <>
        <TextField
          {...register('currentPassword')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={loading}
          error={Boolean(errors.currentPassword)}
          helperText={errors.currentPassword?.message}
          label="Current Password"
          margin="dense"
          size="small"
          type="password"
          fullWidth
        />
        <TextField
          {...register('newPassword')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={loading}
          error={Boolean(errors.newPassword)}
          helperText={errors.newPassword?.message}
          label="New Password"
          margin="dense"
          size="small"
          type="password"
          fullWidth
        />
        <TextField
          {...register('confirmPassword')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={loading}
          error={Boolean(errors.confirmPassword)}
          helperText={errors.confirmPassword?.message}
          label="Confirm Password"
          margin="dense"
          size="small"
          type="password"
          fullWidth
        />
        {error ? <Alert severity="error">{error.response?.data.message}</Alert> : null}
      </>
    );
  };

  return (
    <>
      <Dialog open={true}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Change password</DialogTitle>
          <DialogContent>{renderContent()}</DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              {isSubmitSuccessful ? 'Close' : 'Cancel'}
            </Button>
            {!isSubmitSuccessful ? (
              <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
                Change Password
              </LoadingButton>
            ) : null}
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
